import React, { memo, useCallback, useMemo } from 'react';
import { CompactUserResponse } from '@business/entities/account';
import { OrderTermResponse } from '@business/entities/order';
import { OrgResponse } from '@business/entities/organization';
import { CommonService, HTMLService, OrderService, OrgService, ProposalService, TaxService } from '@business/services';
import { Styles } from '@core/utils/css';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { Badge, Card, CardBody, Col, Row, Table } from 'reactstrap';
import { OrderFormType, OrderLineItemData } from './TimeMaterialForm';
import { LabourTypeResponse } from '@business/entities/labour-type';
import { TextBlock } from '@modules/common/TextBlock';
import { ApplicableSectionFormValue } from '../AddProductOverlay/AddProductOverlay';
import { ProposalDiscountType } from '@business/entities/proposal';
import { TimeMaterialService } from '@business/services/time_material_service';
import { useCompany } from '@core/utils/hooks/misc';

export interface TimeMaterialPreviewProps {
  title?: string;
  form: OrderFormType;
  organization: OrgResponse;
  users: CompactUserResponse[];
  terms?: OrderTermResponse[];
  labourTypes?: LabourTypeResponse[];
  sections: ApplicableSectionFormValue[];
  addFee?: boolean;
  isEdit?: boolean;
}

export const TimeMaterialPreview = memo((props: TimeMaterialPreviewProps) => {
  const { form, users, organization, title = gcLabel('Estimate'), terms = [], labourTypes = [], sections, addFee = false, isEdit = false } = props;
  const formValue = useNonNilObservable(form.value$);
  const lineItemValues = useNonNilObservable(form.controls.lineItems.value$);
  const company = useCompany();

  const billingAddress = useMemo(() => R.find(organization.addresses, a => a.id === formValue?.billingAddress), [formValue, organization.addresses]);
  const shippingAddress = useMemo(
    () => R.find(organization.addresses, a => a.id === formValue?.shippingAddress),
    [formValue, organization.addresses],
  );

  // const getTotalLabourPrice = useCallback((line: OrderLineItemData) => {
  //   const total = line.labourItems.reduce((total, item) => total + item.hour * item.price, 0);
  //   return line.quantity * total;
  // }, []);

  // const getTotalLabourCost = useCallback((line: OrderLineItemData) => {
  //   const total = line.labourItems.reduce((total, item) => total + item.hour * item.cost, 0);
  //   return line.quantity * total;
  // }, []);

  const getLabourType = useCallback(
    (line: OrderLineItemData) => {
      if (line.labourType == '0') {
        return 'Multiple';
      } else {
        const labour = R.find(labourTypes, o => o.id == line.labourType);
        return labour ? labour.name : '';
      }
    },
    [labourTypes],
  );
  // const percentTotal = TimeMaterialService.getFeeTotal(formValue.fees);
  const totalFeeAmt = TimeMaterialService.getFeeTotalAmount(formValue.fees, formValue.lineItems);

  const subTotal = useMemo(() => {
    return formValue.lineItems
      .filter(l => !l.isServicePhase)
      .reduce((total, line) => total + R.toNumber(ProposalService.getSubTotalAddProd(line, false)), 0);
    // return formValue.lineItems.reduce((total, line) => total + R.roundDecimal(line.quantity * line.rate, 2) + getTotalLabourPrice(line), 0);
  }, [formValue.lineItems]);

  const newSubtotal = useMemo(() => {
    if (addFee) {
      return subTotal + totalFeeAmt;
      // return (subTotal * percentTotal) / 100 + subTotal;
    }
    return subTotal;
  }, [addFee, subTotal, totalFeeAmt]);

  const discount = useMemo(() => {
    if (!formValue.discount.isDiscount) return 0;
    if (formValue.discount.type === ProposalDiscountType.fixed) {
      return formValue.discount.value;
    } else {
      // return (formValue.discount.value * newSubtotal) / 100;
      return (formValue.discount.value * subTotal) / 100;
    }
  }, [formValue.discount.isDiscount, formValue.discount.type, formValue.discount.value, subTotal]);

  //   const salesTax = useMemo(() => {
  //     const taxTotal = formValue.lineItems.filter(l => !!l.taxable).reduce((total, line) => total + R.roundDecimal(line.quantity * line.rate, 2), 0);
  //     if (!formValue.salesTax.isSalesTax) return 0;
  //     return (formValue.salesTax.value * (taxTotal - discount)) / 100;
  //   }, [discount, formValue.lineItems, formValue.salesTax.isSalesTax, formValue.salesTax.value]);

  //   const salesTax2 = useMemo(
  //     () => TimeMaterialService.calculateTotalSalesTax(formValue.lineItems, formValue.salesTax.value, formValue.fees),
  //     [formValue.fees, formValue.lineItems, formValue.salesTax.value],
  //   );

  // const getFeeTotal = (percent: number, total: number) => {
  //   return (total * percent) / 100;
  // };

  // const taxableFeeTotal = useMemo(() => {
  //   if (isEdit) {
  //     return 0;
  //   }
  //   const formFees = formValue.fees;
  //   const taxableFees = formFees.filter(f => f.taxable);
  //   return TimeMaterialService.getFeeTotalAmount(taxableFees, formValue.lineItems);
  //   // return taxableFees.reduce((acc, fee) => acc + getFeeTotal(fee.percent, subTotal), 0);
  // }, [formValue.fees, formValue.lineItems, isEdit]);

  const allTaxableItems = useMemo(() => {
    // map formValue.lineItems and if lineItem has packageItems, then map packageItems and return all lineItems
    const allLineItems = formValue.lineItems;
    const allPackageItems = R.flatten(allLineItems.map(l => l.packageItems));

    if (!isEdit) {
      // filter all the items which has package- in product key
      return [...allLineItems, ...allPackageItems].filter(l => l.product && !l.product.includes('package-'));
    }
    return [...allLineItems, ...allPackageItems].filter(l => !!l.taxable);
  }, [formValue.lineItems, isEdit]);

  const salesTax = useMemo(() => {
    const parsedItems = allTaxableItems.map(l => ({
      taxable: l.taxable,
      subtotal: ProposalService.getSubTotalAddProd(l, false) as number,
    }));
    const checkedItems = formValue.fees.filter(item => item.isChecked || (item.isChecked as any) == 'true');
    checkedItems.forEach(o => {
      const subTot = formValue.lineItems.reduce((car, l) => car + (ProposalService.getSubTotalAddProd(l, false, o.feeType) as number), 0);
      const fTotal = (subTot / 100) * o.percent;
      parsedItems.push({ taxable: o.taxable, subtotal: fTotal });
    });
    const disc = {
      ...formValue.discount,
      type: ProposalDiscountType.fixed,
      value: discount,
    };
    return TaxService.totalTaxAmt(parsedItems, disc, formValue.salesTax, true);
    // const taxTotal = allTaxableItems
    //   .filter(l => !!l.taxable)
    //   .filter(l => !l.isServicePhase)
    //   .reduce((total, line) => {
    //     // return total + R.roundDecimal(line.quantity * line.rate, 2);
    //     const lineTotal = ProposalService.getSubTotalAddProd(line, false) as number;
    //     return total + lineTotal;
    //   }, 0);
    // if (!formValue.salesTax.isSalesTax) return 0;
    // return (formValue.salesTax.value * (taxTotal + taxableFeeTotal)) / 100;
  }, [allTaxableItems, discount, formValue.discount, formValue.fees, formValue.lineItems, formValue.salesTax]);

  //   const salesTax = useMemo(() => {
  //     const taxTotal = formValue.lineItems
  //       .filter(l => !!l.taxable)
  //       .reduce((total, line) => {
  //         // return total + R.roundDecimal(line.quantity * line.rate, 2);
  //         const lineTotal = ProposalService.getSubTotalAddProd(line, false) as number;
  //         return total + lineTotal;
  //       }, 0);

  //     if (!formValue.salesTax.isSalesTax) return 0;
  //     return (formValue.salesTax.value * (taxTotal - discount)) / 100;
  //   }, [discount, formValue.lineItems, formValue.salesTax.isSalesTax, formValue.salesTax.value]);

  const stats = useMemo(() => {
    const allLineItems = formValue.lineItems;
    const allLineItems2 = allLineItems.map(l => {
      if (l.packageItems.length > 0) {
        return l.packageItems;
      }
      return l;
    });
    const flatItems = R.flatten(allLineItems2);
    // .filter(l => !l.isServicePhase);
    const productItems = flatItems.filter(l => l.product && l.product.includes('product'));
    const LaborServiceItems = flatItems.filter(l => l.product && l.product.includes('service'));
    const partsCost = productItems.filter(l => !l.isServicePhase).reduce((total, line) => total + ProposalService.getPartsCost(line), 0);
    const labourCost = LaborServiceItems.reduce((total, line) => total + R.roundDecimal(ProposalService.getServiceLaborCost(line), 2), 0);
    const labourHours = LaborServiceItems.reduce((total, line) => total + R.roundDecimal(ProposalService.getTotalLaborHoursAddProd(line), 2), 0);
    const partsPrice = productItems.filter(l => !l.isServicePhase).reduce((total, line) => total + ProposalService.getPartsPrice(line), 0);
    const labourPrice = LaborServiceItems.reduce((total, line) => total + ProposalService.getLaborServicePrice(line), 0);
    const total = subTotal + salesTax - discount;
    const totalCost = partsCost + labourCost;
    const profit = newSubtotal - totalCost;
    const margin = newSubtotal == 0 ? 0 : (profit / newSubtotal) * 100;
    const markup = totalCost == 0 ? 0 : (profit / totalCost) * 100;

    return {
      partsCost,
      labourCost,
      partsPrice,
      labourPrice,
      subTotal,
      totalCost,
      total,
      margin,
      markup,
      profit,
      labourHours,
      newSubtotal,
    };
  }, [discount, formValue.lineItems, newSubtotal, salesTax, subTotal]);

  const totalLaborHours = useMemo(() => {
    return formValue.lineItems.reduce((total, line) => total + R.roundDecimal(ProposalService.getTotalLaborHoursAddProd(line), 2), 0);
  }, [formValue.lineItems]);

  const totalAmount = useMemo(() => {
    return newSubtotal - discount + salesTax;
  }, [discount, salesTax, newSubtotal]);

  const salesPersons = useMemo(() => {
    if (!R.isArray(formValue.salesReps)) {
      return '';
    }

    return (formValue.salesReps || [])
      .map(id => users.find(u => u.id === id))
      .map(u => (u ? CommonService.getFullName(u) : ''))
      .join(', ');
  }, [formValue, users]);

  return (
    <>
      <Card>
        <CardBody>
          <div>
            <>
              <div className="d-flex justify-content-between">
                <div className="invoice-title">
                  <div className="mb-4">
                    <img
                      src={organization.invoiceSettings.logo || Styles.assets('images/logo-dark.png')}
                      style={{ maxHeight: 100, height: 100, width: '100%' }}
                      alt="logo"
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <Badge pill className={`${OrderService.getItemStatusClass('open')} font-size-12 px-2 py-1`}>
                    {R.upperFirst('Open')}
                  </Badge>
                </div>
              </div>
              <hr />
              {!R.isEmpty(formValue.name) && (
                <div className="d-flex mb-2">
                  <strong>{formValue.name}</strong>
                </div>
              )}
              <div className="d-flex mb-2">
                <strong>{R.plural((formValue.salesReps || []).length, 'Salesperson', 'Salesperson')}: </strong>
                <span className="ml-2">{salesPersons}</span>
              </div>
              <Row>
                {formValue.type === 'invoice' && (
                  <Col xs="12">
                    <div className="d-flex justify-content-between">
                      <address>
                        <strong>Customer PO#:</strong> {formValue.customerPoNo}
                        <br />
                        <strong>Terms:</strong> {R.find(terms, t => t.id == formValue.termId)?.name}
                        <br />
                        <strong>Tracking #: </strong> {formValue.trackId}
                        <br />
                      </address>
                      <address className="text-right">
                        <strong>Shipping Via:</strong> {formValue.shippingMethod}
                        <br />
                        <strong>Shipped Date:</strong> &nbsp;{formValue.shippedDate ? toMoment(formValue.shippedDate).format('LL') : null}
                        <br />
                      </address>
                    </div>
                  </Col>
                )}

                <Col xs="12">
                  <div className="d-flex justify-content-between">
                    <address>
                      <strong>Bill To:</strong>
                      <br />
                      <span>{organization.name}</span>
                      <br />
                      {formValue?.billingEmail}
                      <br />
                      {!!billingAddress && HTMLService.toReact(OrgService.getAddressString(billingAddress, '<br/>'))}
                    </address>
                    <address>
                      <strong>Ship To:</strong>
                      <br />
                      <span>{organization.name}</span>
                      <br />
                      {formValue?.billingEmail}
                      <br />
                      {!!shippingAddress && HTMLService.toReact(OrgService.getAddressString(shippingAddress, '<br/>'))}
                    </address>
                  </div>
                </Col>
              </Row>
              {formValue.message && (
                <div className="mb-2 mt-2">
                  <strong>Notes:</strong>
                  <p> {formValue.message}</p>
                </div>
              )}
              <div className="py-2 mt-3">
                <h3 className="font-size-15 font-weight-bold">{title} summary</h3>
              </div>
              {sections.map((section, index) => {
                const sectionLines = lineItemValues.filter(line => line.sectionId == section.id || (line.sectionId == 0 && section.id == -1));
                return (
                  <>
                    <div className="py-2 mt-3" key={index}>
                      <h3 className="font-size-15 font-weight-bold">{section.title}</h3>
                    </div>
                    <div className="table-responsive">
                      <Table className="order-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th style={{ width: '70px' }} colSpan={2}>
                              No.
                            </th>
                            <th>SKU</th>
                            <th className="w-25">Item</th>
                            <th>
                              {OrderService.isUMEnabled() ? (
                                <div className="d-flex align-items-center justify-content-between">
                                  <span>Qty</span>
                                  <span>U/M</span>
                                </div>
                              ) : (
                                <span>Qty</span>
                              )}
                            </th>
                            <th className="text-center">Rate</th>
                            <th>Labor Hours</th>
                            <th>Labor Phase</th>
                            <th>Labor Price</th>
                            <th className="text-right">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sectionLines
                            .filter(l => !l.isServicePhase)
                            .map((line, index) => {
                              return (
                                <>
                                  <tr key={index} className="order-row">
                                    <th>
                                      {((line.lineProduct && !R.isEmpty(line.lineProduct.image)) || line.productImage) && (
                                        <div className="" style={{ width: 40 }}>
                                          <img
                                            src={(line.lineProduct?.image ?? line.productImage) as string}
                                            alt={line.description}
                                            style={{ width: '100%' }}
                                            className="img-fluid"
                                          />
                                        </div>
                                      )}
                                    </th>
                                    <td colSpan={2}>{index + 1}</td>
                                    <td>{line.lineTitle}</td>
                                    <td className="w-25">{line.description}</td>
                                    <td>
                                      {OrderService.isUMEnabled() ? (
                                        <div className="d-flex align-items-center justify-content-between">
                                          <span>{ProposalService.getTotalQtyAddProd(line)}</span>
                                          <span>{line.unitOfMeasure}</span>
                                        </div>
                                      ) : (
                                        <span>{ProposalService.getTotalQtyAddProd(line)}</span>
                                      )}
                                    </td>
                                    <td className="text-center">{ProposalService.getTotalRateAddProd(line)}</td>
                                    <td>
                                      <span>{ProposalService.getTotalLaborHoursAddProd(line)}</span>
                                    </td>
                                    <td>
                                      <span>{getLabourType(line)}</span>
                                    </td>
                                    <td>
                                      <span>{ProposalService.getLabourPriceAddProd(line)}</span>
                                    </td>
                                    <td className="text-right">{ProposalService.getSubTotalAddProd(line)}</td>
                                  </tr>
                                  {line.packageItems.map((lineCh, indexCh) => {
                                    return (
                                      <tr className="order-row" key={indexCh}>
                                        <th>
                                          <div className="ml-2">
                                            {lineCh.lineProduct && !R.isEmpty(lineCh.lineProduct.image) && (
                                              <div className="" style={{ width: 40 }}>
                                                <img
                                                  src={lineCh.lineProduct.image as string}
                                                  alt={lineCh.description}
                                                  style={{ width: '100%' }}
                                                  className="img-fluid"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </th>
                                        <td></td>
                                        <td>{indexCh + 1}</td>
                                        <td>{lineCh.lineTitle}</td>
                                        <td>{lineCh.description}</td>
                                        <td>
                                          {OrderService.isUMEnabled() ? (
                                            <div className="d-flex align-items-center justify-content-between">
                                              <span>{ProposalService.getTotalQtyAddProd(lineCh)}</span>
                                              <span>{lineCh.unitOfMeasure}</span>
                                            </div>
                                          ) : (
                                            <span>{ProposalService.getTotalQtyAddProd(lineCh)}</span>
                                          )}
                                        </td>
                                        <td className="text-center">{ProposalService.getTotalRateAddProd(lineCh)}</td>
                                        <td>
                                          <span>{ProposalService.getTotalLaborHoursAddProd(lineCh)}</span>
                                        </td>
                                        <td>
                                          <span>{getLabourType(lineCh)}</span>
                                        </td>
                                        <td>
                                          <span>{ProposalService.getLabourPriceAddProd(lineCh)}</span>
                                        </td>
                                        <td className="text-right">{ProposalService.getSubTotalAddProd(lineCh)}</td>
                                      </tr>
                                    );
                                  })}
                                </>
                              );
                            })}
                          {sections.length - 1 == index && (
                            <>
                              <tr>
                                <td colSpan={9} className="text-right">
                                  Sub Total
                                </td>
                                <td className="text-right">{Formatter.formatCurrency(newSubtotal, company.currencyCode)}</td>
                              </tr>

                              {discount > 0 && (
                                <tr>
                                  <td colSpan={9} className="text-right">
                                    Discount
                                  </td>
                                  <td className="text-right">{Formatter.formatCurrency(-discount, company.currencyCode)}</td>
                                </tr>
                              )}
                              {salesTax > 0 && (
                                <tr>
                                  <td colSpan={9} className="text-right">
                                    Sales Tax
                                  </td>
                                  <td className="text-right">+{Formatter.formatCurrency(salesTax, company.currencyCode)}</td>
                                </tr>
                              )}
                              <tr>
                                <td colSpan={9} className="border-0 text-right">
                                  <strong>Total</strong>
                                </td>
                                <td className="border-0 text-right">
                                  <h4 className="m-0">{Formatter.formatCurrency(totalAmount, company.currencyCode)}</h4>
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </>
                );
              })}

              <div className="d-flex justify-content-between">
                <div className="d-flex flex-column">
                  <TextBlock className="mb-4" title={'Parts Cost'}>
                    {Formatter.formatCurrency(stats.partsCost, company.currencyCode)}
                  </TextBlock>
                  <TextBlock className="mb-4" title={'Labor Cost'}>
                    {Formatter.formatCurrency(stats.labourCost, company.currencyCode)}
                  </TextBlock>
                  <TextBlock className="mb-4" title={'Total Cost'}>
                    {Formatter.formatCurrency(stats.totalCost, company.currencyCode)}
                  </TextBlock>
                </div>

                <div className="d-flex flex-column">
                  <TextBlock className="mb-4" title={'Parts Price'}>
                    {Formatter.formatCurrency(stats.partsPrice, company.currencyCode)}
                  </TextBlock>
                  <TextBlock className="mb-4" title={'Labor Price'}>
                    {Formatter.formatCurrency(stats.labourPrice, company.currencyCode)}
                  </TextBlock>
                  <TextBlock className="mb-4" title={'Subtotal'}>
                    {Formatter.formatCurrency(newSubtotal, company.currencyCode)}
                  </TextBlock>
                </div>

                <div className="d-flex flex-column">
                  <TextBlock className="mb-4" title={'Profit'}>
                    {Formatter.formatCurrency(stats.profit, company.currencyCode)}
                  </TextBlock>
                </div>

                <div className="d-flex flex-column">
                  <TextBlock className="mb-4" title={'Margin'}>
                    {Formatter.formatPercent(stats.margin, 2)}
                  </TextBlock>
                  <TextBlock className="mb-4" title={'Markup'}>
                    {Formatter.formatPercent(stats.markup, 2)}
                  </TextBlock>
                </div>

                <div className="d-flex flex-column">
                  <TextBlock className="mb-4" title={'Labor Hours'}>
                    {R.roundDecimal(totalLaborHours, 2)}
                  </TextBlock>
                </div>
              </div>
            </>
          </div>
        </CardBody>
      </Card>
    </>
  );
});
